import { ChevronDown, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import {
  type AuditTrailEntry,
  downloadAuditTrailPdf,
  useAuditTrail,
} from '../api/risk-assessment-wizard';

interface AuditTrailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string;
}

/**
 * Format ISO timestamp to full date and time format (DD/MM/YYYY HH:MM:SS)
 */
function formatTimestamp(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  return date.toLocaleString('en-GB', { hour12: false });
}

/** Agent names for Phase 2 (adverse media worker) entries */
const ADVERSE_MEDIA_WORKER_NAMES = new Set([
  'Adverse Media Worker',
  'Enhanced Adverse Media Worker',
]);

/** Header event types shown flat at the top */
const HEADER_EVENT_TYPES = new Set(['screening.initiated', 'input.received']);

/** Footer event types shown flat at the bottom */
const FOOTER_EVENT_TYPES = new Set([
  'screening.completed',
  'screening.failed',
  'screening.final',
  'output.generated',
  'risk.recalculated',
  'assessment.updated',
]);

/** Footer categories (fallback for older data without eventType) */
const FOOTER_CATEGORIES = new Set(['screening.decision', 'screening.output']);

function isHeaderEntry(entry: AuditTrailEntry): boolean {
  return HEADER_EVENT_TYPES.has(entry.eventType);
}

function isFooterEntry(entry: AuditTrailEntry): boolean {
  if (FOOTER_EVENT_TYPES.has(entry.eventType)) return true;
  if (FOOTER_CATEGORIES.has(entry.category)) return true;
  // Lifecycle events that are completion/failure
  if (entry.category === 'screening.lifecycle' && !HEADER_EVENT_TYPES.has(entry.eventType)) {
    return true;
  }
  return false;
}

function isAdverseMediaWorkerEntry(entry: AuditTrailEntry): boolean {
  return ADVERSE_MEDIA_WORKER_NAMES.has(entry.agentName);
}

interface GroupedEntries {
  header: AuditTrailEntry[];
  sections: { label: string; entries: AuditTrailEntry[] }[];
  investigationResults: AuditTrailEntry[];
  phase2: AuditTrailEntry[];
}

/**
 * Extract a human-readable check name from the message text.
 * Messages follow the pattern: "UK sanctions check completed — ..."
 * Falls back to agentName if no pattern matches.
 */
function extractCheckLabel(entry: AuditTrailEntry): string {
  const msg = entry.message;

  // Match patterns like "UK sanctions check", "PEP check", "Adverse media check", etc.
  const match = msg.match(/^(.+?)\s+check\s+(completed|failed|skipped)/i);
  if (match) {
    return match[1];
  }

  return entry.agentName;
}

function groupEntries(entries: AuditTrailEntry[]): GroupedEntries {
  const header: AuditTrailEntry[] = [];
  const investigationResults: AuditTrailEntry[] = [];
  const phase2: AuditTrailEntry[] = [];
  const checkEntries: AuditTrailEntry[] = [];

  for (const entry of entries) {
    if (isAdverseMediaWorkerEntry(entry)) {
      phase2.push(entry);
    } else if (isHeaderEntry(entry)) {
      header.push(entry);
    } else if (entry.category === 'screening.check') {
      checkEntries.push(entry);
    } else if (isFooterEntry(entry)) {
      investigationResults.push(entry);
    } else {
      // Default: treat unknown entries as check entries for grouping
      checkEntries.push(entry);
    }
  }

  // Group check entries by extracted check label, preserving order of first appearance
  const sectionMap = new Map<string, AuditTrailEntry[]>();
  for (const entry of checkEntries) {
    const key = extractCheckLabel(entry);
    if (!sectionMap.has(key)) {
      sectionMap.set(key, []);
    }
    sectionMap.get(key)!.push(entry);
  }

  const sections = Array.from(sectionMap.entries()).map(([label, sectionEntries]) => ({
    label,
    entries: sectionEntries,
  }));

  return { header, sections, investigationResults, phase2 };
}

/**
 * Individual audit entry row component
 */
function AuditEntryRow({ entry }: { entry: AuditTrailEntry }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between">
        <span className="text-[15px] font-semibold text-[#051923]">{entry.agentName}</span>
        <span className="font-mono text-[13px] text-gray-500">
          {formatTimestamp(entry.timestamp)}
        </span>
      </div>
      <p className="mt-1 text-[14px] leading-relaxed text-[#051923]">{entry.message}</p>
    </div>
  );
}

/**
 * Accordion section for a group of check entries
 */
function AccordionSection({
  label,
  entries,
  isExpanded,
  onToggle,
}: {
  label: string;
  entries: AuditTrailEntry[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-3 text-left transition-colors hover:bg-gray-50"
      >
        <span className="text-[15px] font-semibold text-[#051923]">{label}</span>
        <ChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {isExpanded && (
        <div className="px-3 pb-3">
          {entries.map((entry) => (
            <div key={entry.id} className="mb-2 last:mb-0">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[13px] text-gray-500">
                  {formatTimestamp(entry.timestamp)}
                </span>
              </div>
              <p className="mt-0.5 text-[14px] leading-relaxed text-[#051923]">{entry.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AuditTrailModal({ open, onOpenChange, assessmentId }: AuditTrailModalProps) {
  const { data: auditTrail, isLoading, error } = useAuditTrail(assessmentId, open);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const grouped = useMemo(() => {
    if (!auditTrail?.entries.length) return null;
    return groupEntries(auditTrail.entries);
  }, [auditTrail?.entries]);

  const toggleSection = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleExportPdf = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadAuditTrailPdf(assessmentId);
      toast.success('Audit trail exported successfully');
    } catch {
      toast.error('Failed to export audit trail');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-[900px]">
        <DialogHeader className="flex flex-row items-center justify-between pr-10">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            AI Audit Trail
          </DialogTitle>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={isExporting || isLoading}
            className="text-[14px] font-medium text-[#131313] underline hover:text-[#131313]/70 disabled:opacity-50"
          >
            {isExporting ? 'Exporting...' : 'Export as PDF'}
          </button>
        </DialogHeader>

        <div className="mt-4 flex-1 overflow-y-auto pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="py-8 text-center text-red-600">Failed to load audit trail</div>
          ) : !grouped || auditTrail?.entries.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No audit trail entries found</div>
          ) : (
            <div>
              {/* Header entries — shown flat */}
              {grouped.header.length > 0 && (
                <div className="mb-4 space-y-1">
                  {grouped.header.map((entry) => (
                    <AuditEntryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}

              {/* Check entries — grouped by check type in accordion sections */}
              {grouped.sections.length > 0 && (
                <div className="mb-4 rounded-md border border-gray-200">
                  {grouped.sections.map((section) => (
                    <AccordionSection
                      key={section.label}
                      label={section.label}
                      entries={section.entries}
                      isExpanded={expandedKeys.includes(section.label)}
                      onToggle={() => toggleSection(section.label)}
                    />
                  ))}
                </div>
              )}

              {/* Investigation results — shown flat */}
              {grouped.investigationResults.length > 0 && (
                <div className="mb-4 space-y-1">
                  {grouped.investigationResults.map((entry) => (
                    <AuditEntryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}

              {/* Phase 2: Adverse media worker — shown flat */}
              {grouped.phase2.length > 0 && (
                <div className="space-y-1">
                  {grouped.phase2.map((entry) => (
                    <AuditEntryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
